import { Settings } from "./settings";

// Helper: converts multiplier-only config into pixel values
const buildPaddingConfig = (configs) =>
  Object.fromEntries(
    Object.entries(configs).map(([key, { sides, top, bottom }]) => [
      key,
      {
        paddingSides: Settings.SYMBOL_WIDTH * sides,
        paddingTop: Settings.SYMBOL_HEIGHT * top,
        paddingBottom: Settings.SYMBOL_HEIGHT * bottom,
      },
    ]),
  );

export const PixiSettings = {
  // Multipliers only — auto-multiplied by SYMBOL_WIDTH (sides) and SYMBOL_HEIGHT (top/bottom)
  REELSET_LAYOUT_CONFIGS: buildPaddingConfig({
    // Landscape fits the 1920 x 1080 stage: (1920 − 925) / 185 = 5.378.
    // Portrait fits the 9:16 view of 1064.9 x 1893 units.
    MOBILE_SMALL_PORTRAIT: { sides: 0.756, top: 3.116, bottom: 3.116 },
    MOBILE_SMALL_LANDSCAPE: { sides: 5.378, top: 0.919, bottom: 0.919 },
    MOBILE_PORTRAIT: { sides: 0.756, top: 3.116, bottom: 3.116 },
    MOBILE_LANDSCAPE: { sides: 5.378, top: 0.919, bottom: 0.919 },
    TABLET_PORTRAIT: { sides: 0.756, top: 3.116, bottom: 3.116 },
    TABLET_LANDSCAPE: { sides: 5.378, top: 0.919, bottom: 0.919 },
    SMALL_DESKTOP: { sides: 5.378, top: 0.919, bottom: 0.919 },
    DESKTOP: { sides: 5.378, top: 0.919, bottom: 0.919 },
    WIDE_DESKTOP: { sides: 5.378, top: 0.919, bottom: 0.919 },
    ULTRA_WIDE_DESKTOP: { sides: 5.378, top: 0.919, bottom: 0.919 },
  }),

  ORIENTATION: {
    PORTRAIT: "PORTRAIT",
    LANDSCAPE: "LANDSCAPE",
  },

  DEVICE: {
    MOBILE_SMALL: "MOBILE_SMALL",
    MOBILE: "MOBILE",
    TABLET: "TABLET",
    DESKTOP: "DESKTOP",
  },

  /**
   * Centralized font style presets.
   * Only visual appearance (fill, stroke, gradient, shadow) — not size/weight/layout.
   * Change these once per game to restyle all text across every component.
   */
  FONT_STYLES: {
    /**
     * PRIMARY — Two-layer stacked text (shadow bottom + white top).
     * Used by: WinAnimation, IntroPopup, BuyBonusController
     */
    PRIMARY: {
      fontFamily: "Noto Sans, Arial, sans-serif",
      shadow: {
        fill: ["#7b4cbf", "#5a2bb0", "#7b4cbf"],
        fillGradientType: 0,
        stroke: "#3a1878",
        strokeThickness: 4,
        lineJoin: "round",
      },
      top: {
        fill: "#ffffff",
        stroke: "#3a1878",
        strokeThickness: 3,
        lineJoin: "round",
      },
      shadowOffsetY: 3,
    },

    /**
     * SECONDARY — Single-layer text with drop shadow.
     * Used by: ReelFrontController (LINES text)
     */
    SECONDARY: {
      fontFamily: "Noto Sans, Arial, sans-serif",
      fill: "#a1cc02",
      stroke: "#4a5e01",
      strokeThickness: 2,
      dropShadow: true,
      dropShadowColor: "#1a2600",
      dropShadowDistance: 2,
      dropShadowBlur: 0,
      dropShadowAlpha: 1,
      dropShadowAngle: Math.PI / 2,
    },

    /**
     * SLOT_TEXT — Shared layout preset for all spine-slot-attached text.
     * Defines fontWeight, fontSize, padding, and resolution so every
     * slot text (IntroPopup cards, max win, tap-to-start, WinAnimation button)
     * looks identical. Spread PRIMARY first, then SLOT_TEXT to override layout props.
     */
    SLOT_TEXT: {
      fontWeight: "400",
      fontSize: 38,
      align: "center",
      letterSpacing: 0,
      padding: 10,
      resolution: 1,
    },
  },
};

/**
 * Helper: returns true if the device is any mobile phone type (MOBILE or MOBILE_SMALL).
 * Use this in direct conditionals where MOBILE_SMALL should behave the same as MOBILE.
 * For per-device configs, use getResponsiveConfig which auto-falls back MOBILE_SMALL → MOBILE.
 */
export const isMobileDevice = (device) =>
  device === PixiSettings.DEVICE.MOBILE ||
  device === PixiSettings.DEVICE.MOBILE_SMALL;

// ---------------------------------------------------------------------------
// Global font configuration (theme-aware)
// ---------------------------------------------------------------------------
export const FONT_CONFIG = {
  PRIMARY: '"Noto Sans", sans-serif',
  SECONDARY: "Arial",
};

// ---------------------------------------------------------------------------
// Global text style configuration
// ---------------------------------------------------------------------------
export const TEXT_STYLE_CONFIG = {
  OUTLINE: {
    dropShadow: true,
    dropShadowColor: "rgba(0, 0, 0, 0.08)",
    dropShadowBlur: 3,
    dropShadowAngle: Math.PI / 4,
    dropShadowDistance: 0.8,
  },
};

// ---------------------------------------------------------------------------
// Shared UI button sizing (used by toolbar hooks, displays, logos, scrollbar)
// Individual buttons inline their own copy for independent tuning.
// ---------------------------------------------------------------------------
export const USER_INTERFACE_BUTTON_CONFIG = {
  BASE_WIDTH: 58,
  BASE_HEIGHT: 58,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.05,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.575,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.5,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.225,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// ---------------------------------------------------------------------------
// Primary UI button sizing (used by primary theme buttons and menu toolbar)
// ---------------------------------------------------------------------------
export const PRIMARY_USER_INTERFACE_BUTTON_CONFIG = {
  BASE_WIDTH: 36,
  BASE_HEIGHT: 36,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.85,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.65,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.1,
    },
  },
};

// ---------------------------------------------------------------------------
// Shared overlay configuration — every popup and full-screen backdrop.
// Used by: WinAnimationSecondary, GambleDialog, PotCollectionGrandJackpot,
//          PotCollectionMaxWin, PotCollectionOutro
//
// One source, so nothing can darken the game to a different depth than
// anything else. Was 0x090020 at 0.6 for Zeus, then black at 0.85 — which
// read as too heavy against the Mad Labs art in the demo.
// ---------------------------------------------------------------------------
export const POPUP_OVERLAY_CONFIG = {
  COLOR: 0x000000,
  ALPHA: 0.7,
  FADE_DURATION: 350,
};
